import { Link } from "react-router-dom";
import { ArrowUpRight, Building2, GraduationCap, HandCoins } from "lucide-react";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/primitives";
import { CTABand } from "../components/CTABand";

const CATEGORIES = [
    {
        icon: Building2,
        title: "Hotel Partners",
        desc: "Pilot and implement sustainable hospitality practices.",
    },
    {
        icon: GraduationCap,
        title: "Knowledge Partners",
        desc: "Research, education, technology and technical expertise.",
    },
    {
        icon: HandCoins,
        title: "Funding & Strategic Partners",
        desc: "Support pilot projects, research, innovation and organizational development.",
    },
];

const WELCOME = [
    "Hotels and resorts",
    "Tourism organizations",
    "Universities",
    "Researchers",
    "Governments",
    "Sustainability organizations",
    "Technology companies",
    "Food-waste organizations",
    "Community organizations",
    "Corporate sponsors",
    "Philanthropic organizations",
];

const Partners = () => (
    <main>
        <SEO
            title="Partners | Global Green Hotel"
            description="GGH welcomes collaboration with hotels, universities, researchers, governments, technology companies, sponsors and community organizations."
        />
        <PageHero
            testid="partners-hero"
            eyebrow="Partners"
            titleLines={["Building Partnerships for", "Sustainable Hospitality"]}
            description="GGH is an emerging initiative — and partnership opportunities are currently open for organizations that want to help shape what comes next."
        />

        <section data-testid="partner-categories" className="py-24 sm:py-32">
            <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                <Reveal>
                    <Eyebrow>Three Ways to Partner</Eyebrow>
                    <h2 className="font-display mt-4 max-w-3xl text-3xl font-medium tracking-tight text-[#0A1711] sm:text-4xl">
                        Choose the role that fits your organization
                    </h2>
                </Reveal>

                <div className="mt-14 grid gap-4 lg:grid-cols-3" data-testid="partner-category-grid">
                    {CATEGORIES.map(({ icon: Icon, title, desc }, i) => (
                        <Reveal key={title} delay={0.08 * i} y={26}>
                            <article
                                data-testid={`partner-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                className="card-lift group flex h-full flex-col rounded-2xl border border-[#E8E6E1] bg-white p-8 transition-colors duration-300 hover:border-[#1A3B2B]"
                            >
                                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#1A3B2B] text-[#D9B96A] transition-colors duration-300 group-hover:bg-[#274E3A]">
                                    <Icon size={24} aria-hidden="true" />
                                </span>
                                <h3 className="font-display mt-8 text-2xl font-medium uppercase tracking-wide text-[#0A1711]">
                                    {title}
                                </h3>
                                <p className="mt-3 flex-1 text-sm leading-relaxed text-[#4A5D54]">{desc}</p>
                                <Link
                                    to="/contact"
                                    data-testid={`partner-cta-${i}`}
                                    className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-[#1A3B2B] transition-colors hover:text-[#A4803F]"
                                >
                                    Become a GGH Partner
                                    <ArrowUpRight size={15} aria-hidden="true" />
                                </Link>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        <section data-testid="partner-welcome" className="grain relative overflow-hidden bg-[#07211A] py-24 sm:py-32">
            <div className="relative z-[2] mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                <Reveal>
                    <Eyebrow dark>Who We Welcome</Eyebrow>
                    <h2 className="font-display mt-4 max-w-3xl text-3xl font-medium tracking-tight text-[#FDFBF7] sm:text-4xl">
                        Collaboration across the hospitality ecosystem
                    </h2>
                </Reveal>
                <Reveal delay={0.15}>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#FDFBF7]/70 md:text-lg">
                        GGH welcomes collaboration with:
                    </p>
                </Reveal>
                <ul className="mt-10 flex flex-wrap gap-3" data-testid="welcome-list">
                    {WELCOME.map((item, i) => (
                        <Reveal key={item} delay={0.04 * i} y={16}>
                            <li className="rounded-full border border-[#C2A160]/30 bg-[#04150D]/60 px-5 py-2.5 text-sm font-semibold text-[#FDFBF7]/85">
                                {item}
                            </li>
                        </Reveal>
                    ))}
                </ul>
                <Reveal delay={0.3}>
                    <p className="font-display mt-12 max-w-2xl text-xl italic leading-relaxed text-[#D9B96A]">
                        Partnership opportunities are currently open.
                    </p>
                </Reveal>
            </div>
        </section>

        <CTABand
            testid="cta-band-partners"
            heading="Become a GGH Partner"
            text="Whether you run a hotel, lead a research program, build technology or fund innovation — there is a place for you in this work."
            primary={{ to: "/contact", label: "Start the Conversation" }}
            secondary={{ to: "/get-involved", label: "Get Involved" }}
        />
    </main>
);

export default Partners;
